import { X, Mail, User, MessageSquare, Clock, Info, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useEffect } from "react";

// Types for the sidebar data
export interface ContactDetailsData {
    id?: string;
    user_name?: string;
    user_email?: string | null;
    user_phone?: string | null;
    user_type?: "lead" | "client" | "unknown" | null;
    priority?: "low" | "medium" | "high" | "urgent" | null;
    sentiment?: "positive" | "neutral" | "negative" | "unknown" | null;
    intent?: string | null;
    next_step?: string | null;
    summary?: string | null;
    tags?: string[] | null;
    estimated_time_saved?: number | null;
    message_count?: number;
    // Additional fields for form data with field_type support
    form_fields?: Array<{
        field_id: string;
        title: string;
        value: string;
        field_type?: string;
    }>;
}

interface ContactDetailsSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    contactData: ContactDetailsData | null;
    isLoading?: boolean;
    title?: string;
}

export const ContactDetailsSidebar = ({
    isOpen,
    onClose,
    contactData,
    isLoading = false,
    title = "Contact Details"
}: ContactDetailsSidebarProps) => {
    console.log("ContactDetailsSidebar render:", { isOpen, contactData: !!contactData, isLoading, title });
    
    // Handle escape key to close modal
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);
    
    if (!isOpen) return null;

    return (
        <>
            {/* Modal Backdrop */}
            <div 
                className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity duration-300"
                onClick={onClose}
                aria-hidden="true"
            />
            
            {/* Modal Content - Slide from right */}
            <div className={`
                fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50
                transform transition-transform duration-300 ease-in-out
                ${isOpen ? 'translate-x-0' : 'translate-x-full'}
                flex flex-col
            `}>
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b bg-white flex-shrink-0">
                    <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                        className="h-8 w-8 p-0 hover:bg-gray-100"
                    >
                        <X className="h-4 w-4" />
                    </Button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto">
                    {isLoading ? (
                        <div className="flex-1 flex items-center justify-center p-6">
                            <Spinner size="lg" className="text-indigo-600" />
                        </div>
                    ) : !contactData ? (
                        <div className="flex-1 flex items-center justify-center p-6 text-center">
                            <div>
                                <Info className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                                <h3 className="text-lg font-medium mb-1 text-slate-800">No Contact Selected</h3>
                                <p className="text-slate-500 text-sm max-w-md">
                                    Select a contact to view details
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col h-full">
                            <div className="flex-1 overflow-auto min-h-0">
                                {/* User Info Section */}
                                <div className="p-6 border-b">
                                    {/* User Name */}
                                    <h2 className="text-[16px] font-semibold text-slate-900 mb-3">
                                        {contactData.user_name || "Anonymous User"}
                                    </h2>
                                    
                                    {/* Contact Details */}
                                    <div className="space-y-2 mb-4">
                                        {/* Email */}
                                        <div className="flex items-center gap-2">
                                            <Mail className="h-4 w-4 text-slate-500" />
                                            <span className="text-sm text-slate-800">
                                                {contactData.user_email || <span className="text-gray-400">Not provided</span>}
                                            </span>
                                        </div>
                                        
                                        {/* Phone */}
                                        <div className="flex items-center gap-2">
                                            <Phone className="h-4 w-4 text-slate-500" />
                                            <span className="text-sm text-slate-800">
                                                {contactData.user_phone || <span className="text-gray-400">Not provided</span>}
                                            </span>
                                        </div>
                                    </div>

                                    {/* User Type Tag */}
                                    {contactData.user_type && (
                                        <div className="mb-4">
                                            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium capitalize ${
                                                contactData.user_type === 'client' ? 'bg-blue-100 text-blue-800' :
                                                contactData.user_type === 'lead' ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-slate-100 text-slate-800'
                                            }`}>
                                                {contactData.user_type}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Contact Insights Section */}
                                <div className="p-6 border-b">
                                    <h3 className="text-sm font-semibold text-slate-900 mb-4">Contact Insights</h3>
                                    
                                    <div className="space-y-4">
                                        {/* Priority */}
                                        {contactData.priority && (
                                            <div>
                                                <p className="text-xs text-slate-500 mb-1">Priority</p>
                                                <span className={`inline-block px-2 py-1 rounded-md text-xs font-medium capitalize ${
                                                    contactData.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                                                    contactData.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                                                    contactData.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                                    contactData.priority === 'low' ? 'bg-green-100 text-green-800' :
                                                    'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {contactData.priority}
                                                </span>
                                            </div>
                                        )}

                                        {/* Sentiment */}
                                        {contactData.sentiment && contactData.sentiment !== 'unknown' && (
                                            <div>
                                                <p className="text-xs text-slate-500 mb-1">Sentiment</p>
                                                <span className={`inline-block px-2 py-1 rounded-md text-xs font-medium capitalize ${
                                                    contactData.sentiment === 'positive' ? 'bg-green-100 text-green-800' :
                                                    contactData.sentiment === 'negative' ? 'bg-red-100 text-red-800' :
                                                    contactData.sentiment === 'neutral' ? 'bg-blue-100 text-blue-800' :
                                                    'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {contactData.sentiment}
                                                </span>
                                            </div>
                                        )}

                                        {/* Intent */}
                                        {contactData.intent && (
                                            <div>
                                                <p className="text-xs text-slate-500 mb-1">Intent</p>
                                                <p className="text-sm text-slate-800">{contactData.intent}</p>
                                            </div>
                                        )}

                                        {/* Next Steps */}
                                        {contactData.next_step && (
                                            <div>
                                                <p className="text-xs text-slate-500 mb-1">Next Steps</p>
                                                <p className="text-sm text-slate-800">{contactData.next_step}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Summary Section */}
                                {contactData.summary && (
                                    <div className="p-6 border-b">
                                        <h3 className="text-sm font-semibold text-slate-900 mb-3">Conversation Summary</h3>
                                        <p className="text-sm text-slate-700 leading-relaxed">
                                            {contactData.summary}
                                        </p>
                                    </div>
                                )}

                                {/* Tags Section */}
                                {contactData.tags && contactData.tags.length > 0 && (
                                    <div className="p-6 border-b">
                                        <h3 className="text-sm font-semibold text-slate-900 mb-3">Tags</h3>
                                        <div className="flex flex-wrap gap-2">
                                            {contactData.tags.map((tag, index) => (
                                                <span key={index} className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md font-medium border-0">
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Form Fields Section */}
                                {contactData.form_fields && contactData.form_fields.length > 0 && (
                                    <div className="p-6 border-b">
                                        {/* Pre-Session Fields */}
                                        {contactData.form_fields.some(field => field.field_type === 'pre-session') && (
                                            <div className="mb-6">
                                                <h3 className="text-sm font-semibold text-slate-900 mb-3">Pre-Chat Information</h3>
                                                <div className="space-y-3">
                                                    {contactData.form_fields
                                                        .filter(field => field.field_type === 'pre-session')
                                                        .map((field) => (
                                                            <div key={field.field_id} className="bg-blue-50 rounded-md p-3 border border-blue-100">
                                                                <h4 className="text-xs font-medium text-blue-700 mb-1">{field.title}</h4>
                                                                <p className="text-sm text-slate-800 break-words">{field.value}</p>
                                                            </div>
                                                        ))}
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* In-Conversation Fields */}
                                        {contactData.form_fields.some(field => field.field_type === 'in_conversation' || !field.field_type) && (
                                            <div className="mb-6">
                                                <h3 className="text-sm font-semibold text-slate-900 mb-3">Conversation Fields</h3>
                                                <div className="space-y-3">
                                                    {contactData.form_fields
                                                        .filter(field => field.field_type === 'in_conversation' || !field.field_type)
                                                        .map((field) => (
                                                            <div key={field.field_id} className="bg-slate-50 rounded-md p-3">
                                                                <h4 className="text-xs font-medium text-slate-600 mb-1">{field.title}</h4>
                                                                <p className="text-sm text-slate-800 break-words">{field.value}</p>
                                                            </div>
                                                        ))}
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Post-Conversation Fields */}
                                        {contactData.form_fields.some(field => field.field_type === 'post_conversation') && (
                                            <div>
                                                <h3 className="text-sm font-semibold text-slate-900 mb-3">Extracted Information</h3>
                                                <div className="space-y-3">
                                                    {contactData.form_fields
                                                        .filter(field => field.field_type === 'post_conversation')
                                                        .map((field) => (
                                                            <div key={field.field_id} className="bg-purple-50 rounded-md p-3 border border-purple-100">
                                                                <h4 className="text-xs font-medium text-purple-700 mb-1">{field.title}</h4>
                                                                <p className="text-sm text-slate-800 break-words">{field.value}</p>
                                                            </div>
                                                        ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Stats Section */}
                                <div className="p-6">
                                    <div className="grid grid-cols-2 gap-4">
                                        {/* Messages Count */}
                                        <div className="text-center">
                                            <div className="flex items-center justify-center gap-1 mb-1">
                                                <MessageSquare className="h-4 w-4 text-slate-500" />
                                                <p className="text-xs text-slate-500">Messages</p>
                                            </div>
                                            <p className="text-lg font-semibold text-slate-800">
                                                {contactData.message_count || 0}
                                            </p>
                                        </div>
                                        
                                        {/* Time Saved */}
                                        <div className="text-center">
                                            <div className="flex items-center justify-center gap-1 mb-1">
                                                <Clock className="h-4 w-4 text-slate-500" />
                                                <p className="text-xs text-slate-500">Time Saved</p>
                                            </div>
                                            <p className="text-lg font-semibold text-blue-600">
                                                {contactData.estimated_time_saved ? `${contactData.estimated_time_saved}m` : '0m'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}; 